import React, { useState, useEffect, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  Upload,
  Wallet,
  ShoppingBag,
  Image as ImageIcon,
  Download,
  Loader2,
  UploadCloud,
  CheckCircle2,
  Clock,
  XCircle,
} from "lucide-react";
import api, { assetImage, formatIDR, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import BankSelect from "@/components/BankSelect";
import defaultBanks from "@/lib/banks.json";

export default function ContributorStudio() {
  const { t, lang, localizedTitle } = useI18n();
  const [assets, setAssets] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [bankList, setBankList] = useState(defaultBanks);
  const [wform, setWform] = useState({ amount: "", bank_code: "BANK BCA", account_no: "", account_name: "" });
  const [withdrawing, setWithdrawing] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    title_id: "",
    title_en: "",
    description_id: "",
    description_en: "",
    category: "Photos",
    asset_type: "photo",
    tags: "",
    price: "50000",
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.get("/contributor/assets").then((r) => setAssets(r.data.assets));
    api.get("/contributor/earnings").then((r) => setEarnings(r.data));
    api.get("/contributor/withdrawals").then((r) => setWithdrawals(r.data.withdrawals));
    api.get("/banks").then((r) => {
      if (r.data?.banks?.length) setBankList(r.data.banks);
    }).catch(() => {});
  };
  useEffect(load, []);

  const submitWithdraw = async (e) => {
    e.preventDefault();
    setWithdrawing(true);
    try {
      await api.post("/contributor/withdraw", {
        amount: parseInt(wform.amount, 10),
        bank_code: wform.bank_code,
        account_no: wform.account_no,
        account_name: wform.account_name,
      });
      toast.success(t("studio.withdrawSuccess"));
      setWform({ ...wform, amount: "", account_no: "", account_name: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setWithdrawing(false);
    }
  };

  const onFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error(t("studio.dropFile"));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await api.post("/contributor/assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("studio.uploadSuccess"));
      setForm({ ...form, title_id: "", title_en: "", description_id: "", description_en: "", tags: "" });
      setFile(null);
      setPreview(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      approved: { c: "bg-emerald-100 text-emerald-700", i: CheckCircle2, l: t("studio.statusApproved") },
      pending: { c: "bg-amber-100 text-amber-700", i: Clock, l: t("studio.statusPending") },
      rejected: { c: "bg-red-100 text-red-700", i: XCircle, l: t("studio.statusRejected") },
    };
    const s = map[status] || map.pending;
    const Icon = s.i;
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${s.c}`}>
        <Icon size={12} /> {s.l}
      </span>
    );
  };

  const categories = ["Photos", "Architecture", "Portrait", "Landscape", "3D Graphic", "Illustration", "Templates", "Vectors"];

  return (
    <div className="mx-auto max-w-7xl px-6 py-10">
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{t("studio.title")}</h1>
      <p className="mt-1 text-sm text-slate-500">{t("studio.subtitle")}</p>

      {/* Stats */}
      {earnings && (
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { icon: Wallet, label: t("studio.totalEarnings"), value: formatIDR(earnings.total_earnings), color: "text-emerald-600" },
            { icon: ShoppingBag, label: t("studio.totalSales"), value: earnings.total_sales, color: "text-blue-600" },
            { icon: ImageIcon, label: t("studio.totalAssets"), value: earnings.asset_count, color: "text-fuchsia-600" },
            { icon: Download, label: t("studio.totalDownloads"), value: earnings.downloads, color: "text-amber-600" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-5">
                <Icon size={20} className={s.color} />
                <p className="mt-3 font-display text-2xl font-extrabold text-slate-900">{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            );
          })}
        </div>
      )}

      <Tabs defaultValue="upload" className="mt-8">
        <TabsList data-testid="studio-tabs">
          <TabsTrigger value="upload" data-testid="tab-upload">{t("studio.upload")}</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets">{t("studio.myAssets")}</TabsTrigger>
          <TabsTrigger value="earnings" data-testid="tab-earnings">{t("studio.earnings")}</TabsTrigger>
          <TabsTrigger value="withdraw" data-testid="tab-withdraw">{t("studio.withdrawTab")}</TabsTrigger>
        </TabsList>

        {/* Upload */}
        <TabsContent value="upload">
          <form onSubmit={submit} className="mt-6 grid gap-6 lg:grid-cols-2">
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                onFile(e.dataTransfer.files?.[0]);
              }}
              className="flex min-h-[300px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-white p-6 text-center hover:border-blue-500 transition"
              data-testid="upload-dropzone"
            >
              {preview ? (
                <img src={preview} alt="" className="max-h-64 rounded-lg object-contain" />
              ) : (
                <>
                  <UploadCloud size={40} className="text-slate-300" />
                  <p className="mt-3 font-semibold text-slate-700">{t("studio.dropFile")}</p>
                  <p className="mt-1 text-xs text-slate-400">{t("studio.fileHint")}</p>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])}
                data-testid="upload-file-input"
              />
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("studio.titleId")} value={form.title_id} onChange={(v) => setForm({ ...form, title_id: v })} testid="upload-title-id" required />
                <Field label={t("studio.titleEn")} value={form.title_en} onChange={(v) => setForm({ ...form, title_en: v })} testid="upload-title-en" required />
              </div>
              <Field label={t("studio.descId")} value={form.description_id} onChange={(v) => setForm({ ...form, description_id: v })} testid="upload-desc-id" textarea />
              <Field label={t("studio.descEn")} value={form.description_en} onChange={(v) => setForm({ ...form, description_en: v })} testid="upload-desc-en" textarea />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">{t("studio.category")}</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    data-testid="upload-category"
                  >
                    {categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">{t("studio.type")}</label>
                  <select
                    value={form.asset_type}
                    onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    data-testid="upload-type"
                  >
                    <option value="photo">{t("common.photo")}</option>
                    <option value="graphic">{t("common.graphic")}</option>
                  </select>
                </div>
              </div>
              <Field label={t("studio.tags")} value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} testid="upload-tags" />
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">{t("studio.price")}</label>
                <input
                  type="number"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                  data-testid="upload-price"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={uploading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white hover:bg-slate-800 transition disabled:opacity-60"
                data-testid="upload-submit-btn"
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {uploading ? t("studio.submitting") : t("studio.submit")}
              </button>
            </div>
          </form>
        </TabsContent>

        {/* Assets */}
        <TabsContent value="assets">
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {assets.map((a) => (
              <div key={a.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`studio-asset-${a.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img src={assetImage(a)} alt="" className="h-full w-full object-cover" />
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="line-clamp-1 text-sm font-semibold text-slate-900">{localizedTitle(a)}</h3>
                  </div>
                  <div className="mt-2">{statusBadge(a.status)}</div>
                  {a.status === "rejected" && a.rejection_reason && (
                    <p className="mt-2 text-xs text-red-500">{a.rejection_reason}</p>
                  )}
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="font-mono font-bold text-slate-900">{formatIDR(a.price)}</span>
                    <span className="flex items-center gap-1 text-xs text-slate-400">
                      <Download size={11} /> {a.downloads}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {assets.length === 0 && (
              <p className="col-span-full py-10 text-center text-sm text-slate-500">{t("library.empty")}</p>
            )}
          </div>
        </TabsContent>

        {/* Earnings */}
        <TabsContent value="earnings">
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="text-sm font-semibold text-slate-900">{t("studio.earningsChart")}</h3>
            <div className="mt-6 h-72">
              {earnings && earnings.chart.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={earnings.chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v / 1000}k`} />
                    <Tooltip formatter={(v) => formatIDR(v)} cursor={{ fill: "#f8fafc" }} />
                    <Bar dataKey="earnings" fill="#2563EB" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-slate-400">
                  {lang === "id" ? "Belum ada data penghasilan" : "No earnings data yet"}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Withdraw */}
        <TabsContent value="withdraw">
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex items-center justify-between rounded-xl bg-slate-900 p-5 text-white">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-400">{t("studio.availableBalance")}</p>
                  <p className="mt-1 font-display text-3xl font-extrabold" data-testid="available-balance">
                    {formatIDR(earnings?.available_balance || 0)}
                  </p>
                </div>
                <Wallet size={32} className="text-emerald-400" />
              </div>
              <p className="mt-2 text-xs text-slate-400">
                {t("studio.withdrawn")}: {formatIDR(earnings?.withdrawn || 0)}
              </p>

              <form onSubmit={submitWithdraw} className="mt-5 space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">{t("studio.amount")}</label>
                  <input
                    type="number"
                    min="10000"
                    required
                    value={wform.amount}
                    onChange={(e) => setWform({ ...wform, amount: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    data-testid="withdraw-amount"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">{t("studio.minWithdraw")}</p>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">{t("studio.bankCode")}</label>
                  <BankSelect
                    value={wform.bank_code}
                    onChange={(val) => setWform({ ...wform, bank_code: val })}
                    banks={bankList}
                    testId="withdraw-bank"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">{t("studio.accountNo")}</label>
                  <input
                    required
                    value={wform.account_no}
                    onChange={(e) => setWform({ ...wform, account_no: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    data-testid="withdraw-account-no"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">{t("studio.accountName")}</label>
                  <input
                    required
                    value={wform.account_name}
                    onChange={(e) => setWform({ ...wform, account_name: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    data-testid="withdraw-account-name"
                  />
                </div>
                <button
                  type="submit"
                  disabled={withdrawing}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-semibold text-white hover:bg-emerald-700 transition disabled:opacity-60"
                  data-testid="withdraw-submit-btn"
                >
                  {withdrawing ? <Loader2 size={16} className="animate-spin" /> : <Wallet size={16} />}
                  {t("studio.requestWithdraw")}
                </button>
              </form>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="text-sm font-semibold text-slate-900">{t("studio.withdrawHistory")}</h3>
              <div className="mt-4 space-y-3">
                {withdrawals.length === 0 ? (
                  <p className="py-8 text-center text-sm text-slate-400">{t("studio.noWithdrawals")}</p>
                ) : (
                  withdrawals.map((w) => (
                    <div key={w.id} className="flex items-center justify-between rounded-lg border border-slate-100 p-3" data-testid={`withdrawal-${w.id}`}>
                      <div>
                        <p className="font-mono text-sm font-bold text-slate-900">{formatIDR(w.amount)}</p>
                        <p className="text-xs text-slate-500">{w.bank_code}{w.bank_id ? ` (Bank ID: ${w.bank_id})` : ""} · {w.account_no}</p>
                      </div>
                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                          w.status === "success"
                            ? "bg-emerald-100 text-emerald-700"
                            : w.status === "failed"
                            ? "bg-red-100 text-red-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {t(`withdrawStatus.${w.status}`)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value, onChange, testid, textarea, required }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-slate-600">{label}</label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
          data-testid={testid}
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
          data-testid={testid}
        />
      )}
    </div>
  );
}
